/**
 * Менеджер для управления состоянием listening слоёв во время pan/zoom.
 * Использует debounce для восстановления listening после завершения жеста.
 */
class PanZoomOptimizer {
    constructor() {
        this.timeoutId = null;
        this.stage = null;
    }
    /**
     * Начинает оптимизацию pan/zoom операции.
     * Отключает listening у всех слоёв.
     *
     * @param stage - Konva Stage
     */
    start(stage) {
        this.stage = stage;
        const layers = stage.getLayers();
        // Отключаем listening у всех слоёв для оптимизации
        layers.forEach((layer) => {
            layer.listening(false);
        });
        // Отменяем предыдущий таймаут, если он был
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
    }
    /**
     * Завершает оптимизацию и планирует восстановление listening через idle timeout.
     */
    end() {
        if (!this.stage)
            return;
        // Отменяем предыдущий таймаут, если он был
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
        }
        // Планируем восстановление listening через ~100 мс после последнего обновления
        this.timeoutId = setTimeout(() => {
            if (!this.stage)
                return;
            requestAnimationFrame(() => {
                const layers = this.stage.getLayers();
                layers.forEach((layer) => {
                    layer.listening(true);
                    // Принудительно перерисовываем слой через batchDraw
                    layer.batchDraw();
                });
            });
            this.timeoutId = null;
        }, 100); // ~100 мс задержка для idle состояния
    }
    /**
     * Немедленно восстанавливает listening (для критичных операций).
     */
    flush() {
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        if (!this.stage)
            return;
        const layers = this.stage.getLayers();
        layers.forEach((layer) => {
            layer.listening(true);
            layer.batchDraw();
        });
    }
    /**
     * Очищает все таймауты и восстанавливает состояние.
     */
    cleanup() {
        if (this.timeoutId !== null) {
            clearTimeout(this.timeoutId);
            this.timeoutId = null;
        }
        this.flush();
        this.stage = null;
    }
}
// Глобальный экземпляр оптимизатора
const panZoomOptimizer = new PanZoomOptimizer();
/**
 * Оптимизирует pan/zoom операции, отключая listening у слоёв.
 * После завершения операции восстанавливает состояние через requestAnimationFrame.
 *
 * @param stage - Konva Stage
 * @param fn - Функция, выполняющая pan/zoom операцию
 */
export const withFastPan = (stage, fn) => {
    panZoomOptimizer.start(stage);
    fn();
    panZoomOptimizer.end();
};
/**
 * Начинает оптимизацию pan/zoom (для использования в начале жеста).
 *
 * @param stage - Konva Stage
 */
export const startFastPan = (stage) => {
    panZoomOptimizer.start(stage);
};
/**
 * Завершает оптимизацию pan/zoom (для использования в конце жеста).
 */
export const endFastPan = () => {
    panZoomOptimizer.end();
};
/**
 * Немедленно восстанавливает listening (для критичных операций).
 */
export const flushFastPan = () => {
    panZoomOptimizer.flush();
};
/**
 * Троттлер на основе requestAnimationFrame для массовых обновлений.
 * Гарантирует, что функция будет вызвана максимум один раз за кадр.
 */
export class RAFThrottler {
    constructor() {
        this.rafId = null;
        this.pendingFn = null;
    }
    /**
     * Планирует выполнение функции через requestAnimationFrame.
     * Если функция уже запланирована, заменяет её новой.
     *
     * @param fn - Функция для выполнения
     */
    schedule(fn) {
        this.pendingFn = fn;
        if (this.rafId === null) {
            this.rafId = requestAnimationFrame(() => {
                if (this.pendingFn) {
                    this.pendingFn();
                    this.pendingFn = null;
                }
                this.rafId = null;
            });
        }
    }
    /**
     * Отменяет запланированное выполнение
     */
    cancel() {
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        this.pendingFn = null;
    }
    /**
     * Выполняет функцию немедленно, если она запланирована, и отменяет планирование
     */
    flush() {
        if (this.pendingFn) {
            this.pendingFn();
            this.pendingFn = null;
        }
        this.cancel();
    }
}
/**
 * Создаёт троттлер для массовых обновлений слоёв.
 * Используется для батчинга множественных draw() вызовов в один batchDraw().
 *
 * @param stage - Konva Stage
 * @returns Функция для планирования перерисовки всех слоёв
 */
export const createLayerBatchDrawer = (stage) => {
    const throttler = new RAFThrottler();
    return {
        /**
         * Планирует перерисовку всех слоёв через batchDraw
         */
        scheduleDraw: () => {
            throttler.schedule(() => {
                const layers = stage.getLayers();
                layers.forEach((layer) => {
                    layer.batchDraw();
                });
            });
        },
        /**
         * Немедленно перерисовывает все слои
         */
        drawNow: () => {
            throttler.flush();
            const layers = stage.getLayers();
            layers.forEach((layer) => {
                layer.batchDraw();
            });
        },
        /**
         * Отменяет запланированную перерисовку
         */
        cancel: () => {
            throttler.cancel();
        },
    };
};
